import { IBroadcastFormRecipientResponseReportRow } from 'types/BroadcastForm'

/**
 * Result of NPS calculation with detailed breakdown
 */
export interface NPSScoreDetails {
  nps: number // The final NPS score (-100 to +100)
  promoters: number // Count of promoters (9-10)
  passives: number // Count of passives (7-8)
  detractors: number // Count of detractors (0-6)
  total: number // Total valid responses
  promoterPercentage: number // Percentage of promoters
  passivePercentage: number // Percentage of passives
  detractorPercentage: number // Percentage of detractors
}

/**
 * Calculates the Net Promoter Score (NPS) from Net Promoter responses
 *
 * NPS Calculation:
 * - Promoters: Customers who gave a score of 9 or 10
 * - Passives: Customers who gave a score of 7 or 8 (not included in calculation)
 * - Detractors: Customers who gave a score of 0-6
 * - NPS = (Percentage of Promoters) - (Percentage of Detractors)
 *
 * @param responses - Array of response rows, filtered to only Net Promoter questions
 * @returns The NPS score (ranges from -100 to +100), or null if no valid responses
 */
export const calculateNPS = (
  responses: IBroadcastFormRecipientResponseReportRow[],
): number | null => {
  // Filter to only Net Promoter responses with valid OptionValue
  // Check both ScaleType and QuestionCategory for Net Promoter identification
  const npsResponses = responses.filter(
    (row) => (row.ScaleType === 'Net Promoter' || row.QuestionCategory === 'Net Promoter')
      && row.OptionValue != null
      && row.OptionValue >= 0
      && row.OptionValue <= 10,
  )

  if (npsResponses.length === 0) {
    return null
  }

  // Categorize responses
  let promoters = 0 // 9-10
  let detractors = 0 // 0-6
  // Passives (7-8) are counted in total but not used in calculation

  npsResponses.forEach((row) => {
    const value = row.OptionValue!
    if (value >= 9 && value <= 10) {
      promoters += 1
    } else if (value >= 0 && value <= 6) {
      detractors += 1
    }
    // Passives (7-8) are ignored
  })

  const total = npsResponses.length

  // Calculate percentages
  const promoterPercentage = (promoters / total) * 100
  const detractorPercentage = (detractors / total) * 100

  // Calculate NPS
  const nps = promoterPercentage - detractorPercentage

  // Round to nearest integer
  return Math.round(nps)
}

/**
 * Calculates detailed NPS breakdown for a specific question's responses
 *
 * @param responses - Array of response rows for a single question
 * @returns Detailed NPS breakdown, or null if no valid responses
 */
export const calculateNPSScoreDetails = (
  responses: IBroadcastFormRecipientResponseReportRow[],
): NPSScoreDetails | null => {
  // Filter to responses with valid OptionValue in 0-10 range
  const validResponses = responses.filter(
    (row) => row.OptionValue != null
      && row.OptionValue >= 0
      && row.OptionValue <= 10,
  )

  if (validResponses.length === 0) {
    return null
  }

  // Categorize responses
  let promoters = 0 // 9-10
  let passives = 0 // 7-8
  let detractors = 0 // 0-6

  validResponses.forEach((row) => {
    const value = row.OptionValue!
    if (value >= 9 && value <= 10) {
      promoters += 1
    } else if (value >= 7 && value <= 8) {
      passives += 1
    } else if (value >= 0 && value <= 6) {
      detractors += 1
    }
  })

  const total = validResponses.length

  // Calculate percentages
  const promoterPercentage = (promoters / total) * 100
  const passivePercentage = (passives / total) * 100
  const detractorPercentage = (detractors / total) * 100

  // Calculate NPS
  const nps = promoterPercentage - detractorPercentage

  return {
    nps: Math.round(nps),
    promoters,
    passives,
    detractors,
    total,
    promoterPercentage,
    passivePercentage,
    detractorPercentage,
  }
}

/**
 * Checks if a question is a Net Promoter question based on its properties
 *
 * @param scaleType - The scale type of the question
 * @param questionCategory - The category of the question
 * @returns true if the question is a Net Promoter question
 */
export const isNetPromoterQuestion = (
  scaleType: string,
  questionCategory: string,
): boolean => scaleType === 'Net Promoter' || questionCategory === 'Net Promoter'
